import React, { useState } from 'react';
import { ArrowLeft, Search, MapPin, Phone, Globe, BarChart3, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const RESULT_LIMITS = [20, 50, 100, 150, 200];

const LeadScraperDashboard = () => {
  const navigate = useNavigate();
  const [businessCategory, setBusinessCategory] = useState('dentist');
  const [location, setLocation] = useState('Bangalore');
  const [maxResults, setMaxResults] = useState(100);
  const [minRating, setMinRating] = useState(0);
  const [hasWebsite, setHasWebsite] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!businessCategory.trim() || !location.trim()) {
      toast.error('Business type and location are required.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-scraper/search-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          business_category: businessCategory.trim(),
          location: location.trim(),
          max_results: parseInt(maxResults, 10),
          min_rating: parseFloat(minRating) || 0,
          has_website: hasWebsite,
          has_phone: hasPhone,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Search failed');
      }
      const data = await resp.json();
      const list = data.results || [];
      setResults(list);
      toast.success(`Found ${list.length} leads (you asked for up to ${maxResults}).`);
    } catch (err) {
      toast.error(err.message || 'Failed to search leads');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-12" data-testid="lead-scraper-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
            data-testid="lead-scraper-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-violet-400" />
              Lead Scraper
            </h1>
            <p className="text-zinc-400 text-sm">
              Search local businesses from Google Maps. Pick how many results you want (up to 200).
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <form onSubmit={handleSearch} className="glass p-6 rounded-2xl space-y-4" data-testid="lead-scraper-form">
            <div>
              <label className="block text-sm font-medium mb-1">Business Type / Keyword</label>
              <Input
                value={businessCategory}
                onChange={(e) => setBusinessCategory(e.target.value)}
                placeholder="dentist, salon, restaurant..."
                data-testid="lead-scraper-category"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Location (city / area)</label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Bangalore, Mumbai, New York..."
                data-testid="lead-scraper-location"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Number of Results</label>
              <select
                value={maxResults}
                onChange={(e) => setMaxResults(parseInt(e.target.value, 10))}
                data-testid="lead-scraper-max-results"
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm px-3 outline-none"
              >
                {RESULT_LIMITS.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1">
                Google Maps may return fewer if the area has limited matches.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                Minimum Rating <Star className="w-3 h-3 text-amber-400" />
              </label>
              <select
                value={minRating}
                onChange={(e) => setMinRating(parseFloat(e.target.value))}
                data-testid="lead-scraper-min-rating"
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm px-3 outline-none"
              >
                <option value={0}>Any</option>
                <option value={3}>3.0+</option>
                <option value={3.5}>3.5+</option>
                <option value={4}>4.0+</option>
                <option value={4.5}>4.5+</option>
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={hasWebsite}
                onChange={(e) => setHasWebsite(e.target.checked)}
                data-testid="lead-scraper-has-website"
              />
              Only with website
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={hasPhone}
                onChange={(e) => setHasPhone(e.target.checked)}
                data-testid="lead-scraper-has-phone"
              />
              Only with phone
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 mt-2"
              data-testid="lead-scraper-submit"
            >
              <Search className="w-4 h-4" />
              {loading ? `Scraping ${maxResults}…` : `Search ${maxResults} Leads`}
            </Button>
          </form>
        </div>

        <div className="lg:col-span-2">
          {results.length === 0 ? (
            <div className="h-full flex items-center justify-center text-zinc-500 text-sm" data-testid="lead-scraper-empty">
              Run a search to see leads here.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400" data-testid="lead-scraper-results-count">
                Showing {results.length} leads near {location} (requested {maxResults}).
              </p>
              <div className="space-y-3" data-testid="lead-scraper-results-list">
                {results.map((lead) => (
                  <div
                    key={lead.place_id || `${lead.name}-${lead.address}`}
                    className="glass p-4 rounded-xl flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                    data-testid="lead-scraper-result-item"
                  >
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-violet-400" />
                        {lead.name}
                      </h3>
                      <p className="text-xs text-zinc-400 mt-1">{lead.address}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-300">
                        {lead.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </span>
                        )}
                        {lead.website && (
                          <a
                            href={lead.website}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-200"
                          >
                            <Globe className="w-3 h-3" />
                            Website
                          </a>
                        )}
                        {lead.rating > 0 && (
                          <span>⭐ {lead.rating.toFixed(1)} ({lead.user_ratings_total || 0})</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-zinc-400">
                      <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">
                        google_maps
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadScraperDashboard;
